// The backend's error envelope, on the receiving end.
//
// crm-app answers every failure with `ErrorResponse.V2` —
// `{ version: "v2", code, message, details, traceId }` — where `code` is a
// STABLE machine value and `message` is prose that may be reworded. So the UI
// branches on `code` and only ever displays the prose. A screen that fails must
// say what went wrong; a blank page is the one outcome that tells a reviewer
// nothing.
//
// The status lives on the HTTP response, not in the body, so `ApiProblem`
// carries it beside the envelope rather than reading it out of one.

import { ErrorResponse, type DialAiErrorCode, type ErrorResponseV2 } from "@crm/shared";

/**
 * One field the server named as wrong.
 *
 * Declared here rather than in the contract because the server does not
 * currently send any: `respondCrmFailure` builds `ErrorResponse.V2(code,
 * message)` and puts the field, when it knows one, into the prose. The shape is
 * still read off `details` so the day the server does carry field errors the
 * forms show them without another change, and until then `serverFieldErrors`
 * simply finds none.
 */
export interface FieldError {
  field: string;
  code: string;
  message: string;
}

/**
 * Every failed request surfaces as this, including transport failures, so a
 * component has exactly one error shape to render. `transport` distinguishes
 * "the API said no" from "the API was never reached" — the second is not the
 * API's fault and should not be reported as though it were.
 */
export class ApiProblem extends Error {
  readonly problem: ErrorResponseV2;
  readonly status: number;
  readonly transport: boolean;

  constructor(problem: ErrorResponseV2, status: number, transport = false) {
    super(problem.message);
    this.name = "ApiProblem";
    this.problem = problem;
    this.status = status;
    this.transport = transport;
  }

  get code(): DialAiErrorCode {
    return this.problem.code;
  }

  /** The one human sentence the envelope carries. */
  get title(): string {
    return this.problem.message;
  }

  /** The trace id, when the server sent one — what a bug report should quote. */
  get traceId(): string | null {
    return this.problem.traceId;
  }
}

export function isApiProblem(error: unknown): error is ApiProblem {
  return error instanceof ApiProblem;
}

/**
 * Best-effort `code` for a response that is NOT from crm-app. A 502 page from
 * an ingress, or an HTML login redirect, carries no envelope — but the screen
 * still has to say something specific, and the status is the only signal left.
 */
function codeForStatus(status: number): DialAiErrorCode {
  switch (status) {
    case 400:
      return "crm-validation-failed";
    case 401:
      return "unauthorized";
    case 403:
      return "insufficient-scope";
    case 404:
      return "crm-not-found";
    case 409:
      return "crm-conflict";
    case 428:
      return "crm-precondition-required";
    default:
      return "internal-server-error";
  }
}

/** Keep an HTML error page from becoming a wall of markup in a MessageBar. */
function truncate(text: string, max = 300): string {
  const collapsed = text.replace(/\s+/g, " ").trim();
  return collapsed.length <= max ? collapsed : `${collapsed.slice(0, max)}…`;
}

export interface RawErrorResponse {
  status: number;
  statusText: string;
  /** Parsed JSON body, or the raw text when the body was not JSON. */
  body: unknown;
  url: string;
}

/**
 * Turn a non-2xx response into an `ErrorResponseV2`. A well-formed failure
 * parses cleanly against the generated schema; anything else is synthesized
 * from the status so the UI never has to handle "an error with no shape".
 *
 * Parsed with `ErrorResponse`, the tagged union, rather than the variant alone:
 * the variant's schema describes its own fields and rejects the `version` tag
 * that rides beside them.
 */
export function toErrorResponse(response: RawErrorResponse): ErrorResponseV2 {
  const parsed = ErrorResponse.safeParse(response.body);
  if (parsed.success) {
    const { code, message, details, traceId } = parsed.data;
    return { code, message, details, traceId };
  }

  const text =
    typeof response.body === "string" && response.body.trim() !== ""
      ? truncate(response.body)
      : null;
  const fallbackMessage =
    response.statusText === ""
      ? `Request failed with status ${response.status}`
      : response.statusText;

  return {
    code: codeForStatus(response.status),
    message: text ?? fallbackMessage,
    details: null,
    traceId: null,
  };
}

/** A request that never got a response at all: offline, DNS, aborted socket. */
export function transportProblem(url: string, cause: unknown): ApiProblem {
  const reason = cause instanceof Error ? cause.message : String(cause);
  return new ApiProblem(
    {
      code: "internal-server-error",
      message: `Could not reach the CRM API — the request never completed (${truncate(reason, 120)}).`,
      details: null,
      traceId: null,
    },
    0,
    true,
  );
}

/**
 * The field errors the envelope carries, if any.
 *
 * Two shapes are read, because the server may name one field or several:
 * `details.field` (a single column a CHECK or NOT NULL violation points at) and
 * `details.errors` (a list of `{ field, code, message }`).
 */
export function fieldErrorsOf(problem: ErrorResponseV2): readonly FieldError[] {
  const details = problem.details;
  if (details === null || typeof details !== "object") return [];

  const record: Record<string, unknown> = details;
  const listed = record.errors;
  if (Array.isArray(listed)) {
    return listed.filter((entry): entry is FieldError => {
      if (entry === null || typeof entry !== "object") return false;
      const candidate: Record<string, unknown> = entry;
      return typeof candidate.field === "string" && typeof candidate.message === "string";
    });
  }

  const single = record.field;
  if (typeof single === "string") {
    return [{ field: single, code: problem.code, message: problem.message }];
  }
  return [];
}

export interface DisplayableError {
  title: string;
  detail: string | undefined;
  /** Shown as a small monospace tag so a bug report can quote it. */
  code: string | undefined;
  status: number | undefined;
}

/**
 * Everything a component needs to render a failure, from an error of unknown
 * provenance — a React render crash inside a query, an `ApiProblem`, anything.
 */
export function describeError(error: unknown): DisplayableError {
  if (isApiProblem(error)) {
    return {
      title: error.title,
      detail: error.traceId === null ? undefined : `Trace ${error.traceId}`,
      code: error.code,
      status: error.transport ? undefined : error.status,
    };
  }
  if (error instanceof Error) {
    return {
      title: "Something went wrong",
      detail: error.message,
      code: undefined,
      status: undefined,
    };
  }
  return {
    title: "Something went wrong",
    detail: typeof error === "string" ? error : undefined,
    code: undefined,
    status: undefined,
  };
}
